import { StoreActionMap } from '@/store/types'
import update from 'immutability-helper'
import { updateCartAsync } from '@/modules/Cart/CartServiceHelper'

export interface CartReducer {
  petsitters: string[]
  products: Array<{
    offer: string
    start: string
    end: string
  }>
  animals: string[]
  message: string
  rejectedPetsitters?: {
    '@context': string
    '@id': string
    '@type': string
    petsitter: string
    reason: string
  }[]
  lastUpdate?: number
  allowAutoRebound: boolean
  allowTenders: boolean
  immediateTenders?: boolean
}

export const initialCartReducerState: CartReducer = {
  petsitters: [],
  products: [],
  animals: [],
  message: '',
  rejectedPetsitters: [],
  lastUpdate: Date.now(),
  allowAutoRebound: false,
  allowTenders: true,
  immediateTenders: false,
}

export enum CartTypes {
  Restore = 'Cart/RESTORE',
  AddPetsitter = 'Cart/ADD_PETSITTER',
  RemovePetsitter = 'Cart/REMOVE_PETSITTER',
  ClearCart = 'Cart/CLEAR',
  SetCartFromAPI = 'Cart/SET_FROM_API',
  UpdateCart = 'Cart/UPDATE', // New action type
  Clean = 'Cart/CLEAN',
}

type CartPayload = {
  [CartTypes.Restore]: CartReducer
  [CartTypes.AddPetsitter]: string // petsitterId
  [CartTypes.RemovePetsitter]: string // petsitterId
  [CartTypes.ClearCart]: null
  [CartTypes.SetCartFromAPI]: {
    products: Array<{ offer: string; start: string; end: string }>
    petsitters: string[]
    animals: string[]
    message: string
    rejectedPetsitters: {
      '@context': string
      '@id': string
      '@type': string
      petsitter: string
      reason: string
    }[]
  }
  [CartTypes.UpdateCart]: CartReducer
  [CartTypes.Clean]: null
}

export type CartActions = StoreActionMap<CartPayload>[keyof StoreActionMap<CartPayload>]

const cartReducer = (curState: CartReducer, action: CartActions): CartReducer => {
  const now = Date.now()

  switch (action.type) {
    case CartTypes.Restore:
      if (JSON.stringify(curState) === JSON.stringify(action.payload)) return curState
      return {
        ...action.payload,
        lastUpdate: action.payload.lastUpdate || now,
      }
    case CartTypes.AddPetsitter: {
      const finalState = update(curState, {
        petsitters: { $push: [action.payload] },
        lastUpdate: { $set: now },
      })

      updateCartAsync(finalState)
      return finalState
    }
    case CartTypes.RemovePetsitter: {
      const index = curState.petsitters.findIndex((petsitter) => petsitter === action.payload)
      if (index >= 0) {
        const finalState = update(curState, {
          petsitters: { $splice: [[index, 1]] },
          lastUpdate: { $set: now },
        })

        updateCartAsync(finalState)
        return finalState
      }
      return curState
    }
    case CartTypes.ClearCart:
      return { ...initialCartReducerState, lastUpdate: now }
    case CartTypes.SetCartFromAPI:
      return {
        ...curState,
        products: action.payload.products,
        petsitters: action.payload.petsitters,
        animals: action.payload.animals,
        message: action.payload.message,
        rejectedPetsitters: action.payload.rejectedPetsitters,
        lastUpdate: now,
      }
    case CartTypes.UpdateCart:
      return {
        ...action.payload,
        lastUpdate: now,
      }
    case CartTypes.Clean:
      return { ...initialCartReducerState, lastUpdate: now }
    default:
      return curState
  }
}

export default cartReducer
